import type {
    EditorState,
    ElementNode,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    RangeSelection,
    SerializedTextNode,
} from 'lexical';
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isLineBreakNode,
    $isRangeSelection,
    TextNode,
} from 'lexical';
import {
    getMentionChipClassName,
    resolveMentionToken,
    splitTextWithMentionTokens,
    type MentionLookup,
} from './mentions';

const CHAT_MENTION_NODE_TYPE = 'chat-mention';

interface SerializedChatMentionNode extends SerializedTextNode {
    token: string;
    displayName: string;
    isSelfMention: boolean;
    type: typeof CHAT_MENTION_NODE_TYPE;
    version: 1;
}

interface LexicalPoint {
    key: string;
    offset: number;
    type: 'text' | 'element';
}

interface OffsetLookupResult {
    found: boolean;
    offset: number;
}

interface ChatEditorSnapshot {
    message: string;
    visibleText: string;
    tokenSelectionStart: number | null;
    visibleSelectionStart: number | null;
}

/**
 * Editable Lexical text node that renders a mention chip while preserving the
 * underlying mention token for serialisation.
 */
export class ChatMentionNode extends TextNode {
    __token: string;
    __displayName: string;
    __isSelfMention: boolean;

    static getType() {
        return CHAT_MENTION_NODE_TYPE;
    }

    static clone(node: ChatMentionNode) {
        return new ChatMentionNode(
            node.__token,
            node.__displayName,
            node.__isSelfMention,
            node.__key,
        );
    }

    static importJSON(serializedNode: SerializedChatMentionNode) {
        return $createChatMentionNode({
            token: serializedNode.token,
            displayName: serializedNode.displayName,
            isSelfMention: serializedNode.isSelfMention,
        });
    }

    constructor(
        token: string,
        displayName: string,
        isSelfMention: boolean,
        key?: NodeKey,
    ) {
        super(`@${displayName}`, key);
        this.__token = token;
        this.__displayName = displayName;
        this.__isSelfMention = isSelfMention;
    }

    createDOM(config: Parameters<TextNode['createDOM']>[0]) {
        const dom = super.createDOM(config);
        dom.className = getMentionChipClassName(this.__isSelfMention);
        return dom;
    }

    updateDOM(
        prevNode: ChatMentionNode,
        dom: ReturnType<TextNode['createDOM']>,
        config: Parameters<TextNode['updateDOM']>[2],
    ) {
        const didUpdate = super.updateDOM(prevNode, dom, config);
        if (prevNode.__isSelfMention !== this.__isSelfMention) {
            dom.className = getMentionChipClassName(this.__isSelfMention);
        }
        return didUpdate;
    }

    exportJSON(): SerializedChatMentionNode {
        return {
            ...super.exportJSON(),
            token: this.__token,
            displayName: this.__displayName,
            isSelfMention: this.__isSelfMention,
            type: CHAT_MENTION_NODE_TYPE,
            version: 1,
        };
    }

    getToken() {
        return this.__token;
    }

    getDisplayText() {
        return `@${this.__displayName}`;
    }

    /**
     * Mention chips behave like inline entities: typing at either edge should
     * create a sibling text node instead of mutating the chip and breaking it.
     * Actual edits inside the chip still mutate the node, which allows the
     * transform in `ChatInputComposer` to downgrade it to plain text.
     */
    canInsertTextBefore() {
        return false;
    }

    canInsertTextAfter() {
        return false;
    }
}

function $createChatMentionNode({
    token,
    displayName,
    isSelfMention,
}: {
    token: string;
    displayName: string;
    isSelfMention: boolean;
}) {
    return new ChatMentionNode(token, displayName, isSelfMention);
}

function $isChatMentionNode(
    node: LexicalNode | null | undefined,
): node is ChatMentionNode {
    return node instanceof ChatMentionNode;
}

/**
 * Replace the current editor contents with the canonical chat message string.
 *
 * The rest of chat still treats the message value as plain text with embedded
 * mention tokens, so this function is the main "hydrate Lexical from message"
 * boundary for the composer.
 */
export function setChatEditorStateFromMessage({
    editor,
    message,
    mentionLookup,
    currentUserID,
}: {
    editor: LexicalEditor;
    message: string;
    mentionLookup: MentionLookup;
    currentUserID: number | null;
}) {
    editor.update(
        () => {
            const root = $getRoot();
            root.clear();

            const paragraph = $createParagraphNode();
            root.append(paragraph);
            appendMessageNodes({
                parent: paragraph,
                message,
                mentionLookup,
                currentUserID,
            });
            root.selectEnd();
        },
        { tag: 'chat-sync' },
    );
}

/**
 * Read both editor representations at once:
 * - `message`: the canonical tokenised value persisted by chat
 * - `visibleText`: what the user currently sees in the editor
 *
 * Keeping both in a single snapshot makes it easier to debug mention issues
 * because dropdown logic works from visible text while submit/draft logic uses
 * token text.
 */
export function getChatEditorSnapshot(
    editorState: EditorState,
): ChatEditorSnapshot {
    return editorState.read(() => {
        return {
            message: serializeChatEditor($getRoot(), 'token'),
            visibleText: serializeChatEditor($getRoot(), 'visible'),
            tokenSelectionStart: getSelectionOffset('token'),
            visibleSelectionStart: getSelectionOffset('visible'),
        };
    });
}

/**
 * Replace the currently active visible `@mention` query range with a mention
 * chip followed by a trailing space.
 *
 * This intentionally uses visible-text offsets, not token offsets, because the
 * dropdown opens from what the user can see (`@John...`) rather than the hidden
 * token form (`<@U12>`). The conversion back to token offsets happens when the
 * editor snapshot is serialised.
 */
export function replaceActiveMentionQuery({
    editor,
    token,
    displayName,
    isSelfMention,
}: {
    editor: LexicalEditor;
    token: string;
    displayName: string;
    isSelfMention: boolean;
}) {
    editor.update(
        () => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return;
            }

            const visibleText = serializeChatEditor($getRoot(), 'visible');
            const visibleSelectionStart = getSelectionOffset('visible');
            if (visibleSelectionStart === null) {
                return;
            }

            const mentionStart = getVisibleMentionStartIndex(
                visibleText,
                visibleSelectionStart,
            );
            if (mentionStart === null) {
                return;
            }

            const startPoint = findPointForOffset(
                $getRoot(),
                mentionStart,
                'visible',
            );
            const endPoint = findPointForOffset(
                $getRoot(),
                visibleSelectionStart,
                'visible',
            );
            if (!startPoint || !endPoint) {
                return;
            }

            selection.anchor.set(
                startPoint.key,
                startPoint.offset,
                startPoint.type,
            );
            selection.focus.set(endPoint.key, endPoint.offset, endPoint.type);
            selection.insertNodes([
                $createChatMentionNode({
                    token,
                    displayName,
                    isSelfMention,
                }),
                $createTextNode(' '),
            ]);
        },
        { tag: 'chat-insert-mention' },
    );
}

function appendMessageNodes({
    parent,
    message,
    mentionLookup,
    currentUserID,
}: {
    parent: ElementNode;
    message: string;
    mentionLookup: MentionLookup;
    currentUserID: number | null;
}) {
    for (const segment of splitTextWithMentionTokens(message)) {
        if (segment.type === 'text') {
            appendTextSegment(parent, segment.value);
            continue;
        }

        const mention = resolveMentionToken({
            rawToken: segment.rawToken,
            mentionLookup,
        });
        if (!mention) {
            appendTextSegment(parent, segment.rawToken);
            continue;
        }

        parent.append(
            $createChatMentionNode({
                token: mention.rawToken,
                displayName: mention.displayName,
                isSelfMention:
                    mention.tokenType === 'U' &&
                    currentUserID !== null &&
                    mention.id === currentUserID,
            }),
        );
    }
}

function appendTextSegment(parent: ElementNode, text: string) {
    if (!text) {
        return;
    }

    const lines = text.split('\n');
    for (const [index, line] of lines.entries()) {
        if (line) {
            parent.append($createTextNode(line));
        }
        if (index < lines.length - 1) {
            parent.append($createLineBreakNode());
        }
    }
}

/**
 * Serialise the Lexical tree into either:
 * - `token`: the chat API/storage representation
 * - `visible`: the user-facing text used by mention detection
 *
 * Root-level paragraphs are flattened back into newline-delimited plain text so
 * the chat feature can keep treating the composer as a multiline string.
 */
function serializeChatEditor(
    node: LexicalNode,
    mode: 'token' | 'visible',
): string {
    if ($isChatMentionNode(node)) {
        return mode === 'token' ? node.getToken() : node.getTextContent();
    }
    if ($isLineBreakNode(node)) {
        return '\n';
    }
    if (node instanceof TextNode) {
        return node.getTextContent();
    }
    if (!$isElementNode(node)) {
        return '';
    }

    const children = node.getChildren();
    let text = '';
    for (const [index, child] of children.entries()) {
        const childText = serializeChatEditor(child, mode);
        text += childText;

        if (
            node.getType() === 'root' &&
            index < children.length - 1 &&
            !childText.endsWith('\n')
        ) {
            text += '\n';
        }
    }
    return text;
}

/**
 * Convert the current Lexical focus point into a linear string offset.
 *
 * This is the reverse of `findPointForOffset()`. The important subtlety is that
 * mention nodes count as either their visible length (`@John Test`) or token
 * length (`<@U12>`) depending on which caller needs the result.
 */
function getSelectionOffset(mode: 'token' | 'visible') {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
        return null;
    }

    return getOffsetForSelectionPoint(selection, mode);
}

function getOffsetForSelectionPoint(
    selection: RangeSelection,
    mode: 'token' | 'visible',
) {
    const root = $getRoot();
    const focus = selection.focus;
    const { offset, found } = getOffsetWithinNode(
        root,
        focus.key,
        focus.offset,
        focus.type,
        mode,
    );

    return found ? offset : null;
}

/**
 * Walk the tree to translate a Lexical point into a plain-text offset.
 *
 * This powers token-aware caret reporting for `onChange(text, selectionStart)`,
 * which is why mention nodes cannot simply use their visible length here.
 */
function getOffsetWithinNode(
    node: LexicalNode,
    targetKey: string,
    targetOffset: number,
    targetType: 'text' | 'element',
    mode: 'token' | 'visible',
): OffsetLookupResult {
    if (node.getKey() === targetKey) {
        if ($isElementNode(node) && targetType === 'element') {
            const children = node.getChildren().slice(0, targetOffset);
            const offset = children.reduce((total, child, index) => {
                const childText = serializeChatEditor(child, mode);
                let nextTotal = total + childText.length;
                if (
                    node.getType() === 'root' &&
                    index < children.length - 1 &&
                    !childText.endsWith('\n')
                ) {
                    nextTotal += 1;
                }
                return nextTotal;
            }, 0);
            return { found: true, offset };
        }

        if (node instanceof TextNode && targetType === 'text') {
            return {
                found: true,
                offset: getTextNodeOffset(node, targetOffset, mode),
            };
        }
    }

    if (!$isElementNode(node)) {
        return { found: false, offset: 0 };
    }

    let offset = 0;
    const children = node.getChildren();
    for (const [index, child] of children.entries()) {
        const childResult = getOffsetWithinNode(
            child,
            targetKey,
            targetOffset,
            targetType,
            mode,
        );
        if (childResult.found) {
            return {
                found: true,
                offset: offset + childResult.offset,
            };
        }

        const childText = serializeChatEditor(child, mode);
        offset += childText.length;
        if (
            node.getType() === 'root' &&
            index < children.length - 1 &&
            !childText.endsWith('\n')
        ) {
            offset += 1;
        }
    }

    return { found: false, offset };
}

/**
 * Translate an offset inside a text node into the requested representation.
 *
 * For normal text this is a no-op. For mention chips we approximate the matching
 * token offset so callers can continue to think in `<@...>` coordinates even
 * though the user is editing `@Display Name`.
 */
function getTextNodeOffset(
    node: TextNode,
    targetOffset: number,
    mode: 'token' | 'visible',
) {
    const clampedOffset = Math.min(
        Math.max(targetOffset, 0),
        node.getTextContentSize(),
    );

    if (!$isChatMentionNode(node) || mode === 'visible') {
        return clampedOffset;
    }

    const displayLength = node.getTextContentSize();
    if (displayLength === 0) {
        return 0;
    }

    const tokenLength = node.getToken().length;
    return Math.round((clampedOffset / displayLength) * tokenLength);
}

/**
 * Convert a plain-text offset back into the Lexical point where selection
 * should be placed.
 *
 * This is mainly used when we replace an `@query` with a mention node and need
 * to move Lexical selection without reintroducing DOM-based caret helpers.
 */
function findPointForOffset(
    node: LexicalNode,
    targetOffset: number,
    mode: 'token' | 'visible',
): LexicalPoint | null {
    if (!$isElementNode(node)) {
        return null;
    }

    const clampedTargetOffset = Math.max(targetOffset, 0);
    let remaining = clampedTargetOffset;

    for (const child of node.getChildren()) {
        const childLength = serializeChatEditor(child, mode).length;
        if (remaining <= childLength) {
            if (child instanceof TextNode) {
                return {
                    key: child.getKey(),
                    offset: getTextPointOffset(child, remaining, mode),
                    type: 'text',
                };
            }

            const nestedPoint = findPointForOffset(child, remaining, mode);
            if (nestedPoint) {
                return nestedPoint;
            }

            return {
                key: child.getKey(),
                offset: 0,
                type: 'element',
            };
        }
        remaining -= childLength;
    }

    return {
        key: node.getKey(),
        offset: node.getChildrenSize(),
        type: 'element',
    };
}

/**
 * Reverse the token/display mapping for mention nodes when selection is being
 * positioned from a plain-text offset.
 */
function getTextPointOffset(
    node: TextNode,
    targetOffset: number,
    mode: 'token' | 'visible',
) {
    if (!$isChatMentionNode(node) || mode === 'visible') {
        return Math.min(targetOffset, node.getTextContentSize());
    }

    const tokenLength = node.getToken().length;
    if (tokenLength === 0) {
        return 0;
    }

    const displayLength = node.getTextContentSize();
    return Math.round(
        (Math.min(targetOffset, tokenLength) / tokenLength) * displayLength,
    );
}

/**
 * Find the visible `@` that starts the active mention query.
 *
 * This mirrors the old contentEditable behaviour: mentions only trigger at the
 * start of text or after whitespace/newlines.
 */
function getVisibleMentionStartIndex(text: string, caretIndex: number) {
    if (caretIndex < 0) {
        return null;
    }

    for (let index = caretIndex - 1; index >= 0; index--) {
        if (text[index] !== '@') {
            continue;
        }

        const previousCharacter = index > 0 ? (text.at(index - 1) ?? '') : '';
        if (
            previousCharacter !== '' &&
            previousCharacter !== ' ' &&
            previousCharacter !== '\n'
        ) {
            continue;
        }

        return index;
    }

    return null;
}
